import { useState, useEffect } from "react";
import { Search, MoreVertical, Edit, Trash2, FileText, AlertCircle, Eye, Loader2, Flag, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import api, { errorMessage, getAllPages, logError } from "../services/api";
import { toast } from "react-toastify";
import RefreshButton from "../components/RefreshButton";
import { isSuperAdmin } from "../utils/role";
import { formatLagosDate } from "../utils/tripLog";

// Status mapping from API to UI with case-insensitive matching
const statusMap = {
  // Uppercase variations
  'ACTIVE': 'active',
  'INACTIVE': 'inactive',
  'PENDING': 'pending',
  'SUSPENDED': 'suspended',
  'APPROVED': 'active',
  'REJECTED': 'inactive',
  // Lowercase variations
  'active': 'active',
  'inactive': 'inactive',
  'pending': 'pending',
  'suspended': 'suspended',
  'approved': 'active',
  'rejected': 'inactive'
};

const PAGE_SIZE = 20;

// Format date to DD/MM/YY
// DD/MM/YYYY
const formatDate = (dateString) => formatLagosDate(dateString, 'en-GB', {}, '—');

// Format phone number to display with country code
const formatPhone = (phone) => {
  if (!phone) return '—';
  return phone.startsWith('+') ? phone : `+${phone}`;
};

const Riders = () => {
  const navigate = useNavigate();
  // All customers; the page shown is sliced from this.
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [riderToUpdate, setRiderToUpdate] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [riderToFlag, setRiderToFlag] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  // Ids the server matched for the search (it can match full names that plain admins don't see).
  const [serverMatches, setServerMatches] = useState(null);
  const [searchError, setSearchError] = useState(false);
  const [searchRetry, setSearchRetry] = useState(0);
  const canEditCustomers = isSuperAdmin();

  // Open the edit-customer modal, prefilled from the row's raw data.
  const openEditModal = (rider) => {
    const src = rider.originalData || {};
    setCustomerToEdit(rider);
    setEditForm({
      name: src.name || rider.name || '',
      phone: src.phone || '',
      email: src.email || rider.email || '',
    });
    setShowEditModal(true);
  };

  // Persist edits via PUT /admin/users/:id/profile.
  const handleSaveEdit = async () => {
    if (!customerToEdit) return;
    try {
      setSavingEdit(true);
      const payload = {};
      if (editForm.name?.trim()) payload.name = editForm.name.trim();
      if (editForm.phone?.trim()) payload.phone = editForm.phone.trim();
      if (editForm.email?.trim()) payload.email = editForm.email.trim();
      await api.put(`/v1/admin/users/${customerToEdit.id}/profile`, payload);
      toast.success('Customer updated successfully');
      setShowEditModal(false);
      setCustomerToEdit(null);
      await fetchRiders();
    } catch (error) {
      logError('Error updating customer:', error);
      toast.error(error.response?.data?.message || 'Failed to update customer');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle flag user
  const handleFlagUser = async () => {
    if (!riderToFlag || !flagReason.trim()) {
      toast.error('Please provide a reason for flagging this user');
      return;
    }

    try {
      await api.post(`/v1/admin/users/${riderToFlag}/flag`, {
        reason: flagReason.trim()
      });

      // Refresh the riders list
      fetchRiders();
      toast.success('User has been flagged successfully');
      setShowFlagModal(false);
      setFlagReason('');
      setRiderToFlag(null);
    } catch (error) {
      logError('Error flagging user:', error);
      toast.error(error.response?.data?.message || 'Failed to flag user');
    }
  };

  // Handle unflag user
  const handleUnflagUser = async (userId) => {
    if (!userId) return;

    try {
      await api.post(`/v1/admin/users/${userId}/unflag`);

      // Refresh the riders list
      fetchRiders();
      toast.success('User has been unflagged successfully');
    } catch (error) {
      logError('Error unflagging user:', error);
      toast.error(error.response?.data?.message || 'Failed to unflag user');
    }
  };

  // The users endpoint rejects ?role= (400), so load every user and keep the
  // customers here. Filtering one page instead hid most customers.
  const fetchRiders = async () => {
    try {
      setLoading(true);
      const users = await getAllPages('/v1/admin/users');
      const customers = users
        .filter((user) => user.role === 'customer')
        .map((user) => {
          const id = String(user._id || user.id);
          return {
            id,
            name: user.name || '—',
            // Permanent Zooma code when there is one, else the short account id.
            riderId: user.zoomaId || `…${id.slice(-6)}`,
            email: user.email || '—',
            phone: formatPhone(user.phone || user.phoneNumber),
            status: user.isActive === false ? 'inactive' : 'active',
            flagged: user.flagged === true,
            flagReason: user.flagReason || '',
            date: formatDate(user.createdAt),
            role: user.role,
            originalData: user,
          };
        });
      setRiders(customers);
      setError(null);
      setUpdatedAt(new Date());
    } catch (error) {
      logError('Error fetching customers:', error);
      setError(errorMessage(error, 'Failed to load customers. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiders();
  }, []);

  // Ask the server too, after a short pause in typing.
  useEffect(() => {
    const term = searchTerm.trim();
    // Old matches belong to the old term; drop them right away.
    setServerMatches(null);
    setSearchError(false);
    if (term.length < 3) return undefined;
    let alive = true;
    const id = setTimeout(async () => {
      try {
        const list = await getAllPages('/v1/admin/users', { role: 'customer', search: term });
        if (alive) setServerMatches(new Set(list.map((u) => String(u._id || u.id))));
      } catch (error) {
        logError('Customer search failed:', error);
        if (alive) setSearchError(true);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [searchTerm, searchRetry]);

  const search = searchTerm.toLowerCase();
  const filteredRiders = riders.filter(
    (rider) =>
      !search ||
      rider.name.toLowerCase().includes(search) ||
      rider.riderId.toLowerCase().includes(search) ||
      rider.email.toLowerCase().includes(search) ||
      rider.phone.includes(searchTerm) ||
      serverMatches?.has(rider.id)
  );
  const totalPages = Math.max(1, Math.ceil(filteredRiders.length / PAGE_SIZE));
  const pageNumber = Math.min(currentPage, totalPages);
  const pageRiders = filteredRiders.slice((pageNumber - 1) * PAGE_SIZE, pageNumber * PAGE_SIZE);

  const handleStatusChange = async (rider, status) => {
    setRiderToUpdate(rider);
    setNewStatus(status);
    setActionMessage(status === 'active'
      ? 'Are you sure you want to activate this rider?'
      : 'Are you sure you want to deactivate this rider?');
    setShowStatusModal(true);
  };

  const confirmStatusChange = async () => {
    if (!riderToUpdate) return;

    try {
      setLoading(true);
      // Map UI status back to API status
      const apiStatus = Object.keys(statusMap).find(
        key => statusMap[key] === newStatus
      ) || 'INACTIVE';

      // Update rider status via API
      await api.patch(`/v1/admin/users/${riderToUpdate.id}/status`, {
        status: apiStatus
      });

      // Refresh the riders list
      await fetchRiders();

      toast.success(`Rider ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      logError('Error updating rider status:', error);
      toast.error(error.response?.data?.message || 'Failed to update rider status');
    } finally {
      setLoading(false);
      setShowStatusModal(false);
      setRiderToUpdate(null);
    }
  };

  const cancelStatusChange = () => {
    setShowStatusModal(false);
    setRiderToUpdate(null);
    setNewStatus('');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Render loading state
  if (loading && riders.length === 0) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center w-full lg:pl-64">
          <div className="flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500 mb-2" />
            <p className="text-gray-600">Loading customers...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state (a failed refresh keeps the rows already loaded)
  if (error && riders.length === 0) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center w-full lg:pl-64">
          <div className="text-center p-6 max-w-md">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Passengers</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchRiders}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden w-full lg:pl-64">
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Header */}
          {/* pr-14 keeps the phone menu button (fixed top-right) off the controls. */}
          <div className="flex flex-wrap justify-between items-start gap-3 mb-6 pr-14 lg:pr-0">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">All Passengers</h1>
              <p className="text-sm text-gray-500">
                {riders.length.toLocaleString()} total customers
              </p>
              {!canEditCustomers ? (
                <p className="text-xs text-gray-400 mt-0.5">
                  Names are shortened and contact details hidden for your role.
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              <RefreshButton onRefresh={fetchRiders} loading={loading} updatedAt={updatedAt} />
            </div>
          </div>

          {error ? (
            <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <span>{error} Showing the last loaded customers.</span>
              <button type="button" onClick={fetchRiders} className="font-medium underline">
                Retry
              </button>
            </div>
          ) : null}

          {/* Search */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name, ID, or contact"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-sm"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
              {searchError ? (
                <p role="alert" className="mt-2 text-xs text-red-700">
                  {"Couldn't search all customers, so only names on screen are matched. "}
                  <button type="button" onClick={() => setSearchRetry((n) => n + 1)} className="font-medium underline">
                    Retry
                  </button>
                </p>
              ) : null}
            </div>

          </div>

          {/* Status Change Confirmation Modal */}
          {showStatusModal && riderToUpdate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-black rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {newStatus === 'active' ? 'Activate' : 'Deactivate'} Customer
                </h3>
                <p className="text-gray-600 mb-6">
                  {actionMessage}
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelStatusChange}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmStatusChange}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${newStatus === 'active'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                      }`}
                  >
                    {newStatus === 'active' ? 'Activate' : 'Deactivate'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Riders Table */}
          <div className="bg-white dark:bg-black rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 font-medium text-gray-700 tracking-wider text-left">
                      Customer ID
                    </th>
                    <th className="px-6 py-4 font-medium text-gray-700 tracking-wider text-left">
                      Customer Name
                    </th>
                    <th className="px-6 py-4 font-medium text-gray-700 tracking-wider text-left">
                      Phone Number
                    </th>
                    <th className="px-6 py-4 font-medium text-gray-700 tracking-wider text-left">
                      Email
                    </th>
                    <th className="px-6 py-4 font-medium text-gray-700 tracking-wider text-left">
                      Status
                    </th>
                    <th className="px-6 py-4 font-medium text-gray-700 tracking-wider text-left">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-black divide-y divide-gray-200">
                  {pageRiders.length > 0 ? (
                    pageRiders.map((rider) => (
                      <tr key={rider.id} className={`${rider.flagged ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-50'}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                          {rider.riderId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-300 text-gray-600 font-medium text-sm">
                              {rider.name.charAt(0)}
                            </div>
                            <span className="text-gray-900 font-medium">
                              {rider.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                          {rider.phone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                          {rider.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(rider.status)}`}
                            >
                              {rider.status.charAt(0).toUpperCase() + rider.status.slice(1)}
                            </span>
                            {rider.flagged && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
                                <Flag size={12} className="fill-yellow-500" />
                                Flagged
                              </span>
                            )}
                          </div>
                          {rider.flagged && rider.flagReason ? (
                            <p className="mt-1 max-w-[240px] whitespace-normal break-words text-xs text-gray-500 dark:text-gray-400">
                              {rider.flagReason}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/rider-details/${rider.id}`)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                              title="View Passenger"
                            >
                              <Eye size={16} />
                            </button>
                            {canEditCustomers ? (
                              <button
                                onClick={() => openEditModal(rider)}
                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full"
                                title="Edit Passenger"
                              >
                                <Edit size={16} />
                              </button>
                            ) : null}
                            {rider.flagged ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Are you sure you want to unflag ${rider.name}?`)) {
                                    handleUnflagUser(rider.id);
                                  }
                                }}
                                className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-full"
                                title="Unflag User"
                              >
                                <Flag size={16} fill="currentColor" />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRiderToFlag(rider.id);
                                  setShowFlagModal(true);
                                }}
                                className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-full"
                                title="Flag User"
                              >
                                <Flag size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        No customers found matching your search
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  Showing Page {pageNumber} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(pageNumber - 1, 1))}
                    disabled={pageNumber === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="flex gap-1">
                    <span className="px-4 py-2 rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 font-medium text-sm">
                      {pageNumber}
                    </span>
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(pageNumber + 1, totalPages))}
                    disabled={pageNumber === totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Edit Passenger Modal */}
      {showEditModal && customerToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Passenger
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF8C00] dark:bg-gray-700 dark:text-white"
                  placeholder="Passenger name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF8C00] dark:bg-gray-700 dark:text-white"
                  placeholder="+2348012345678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF8C00] dark:bg-gray-700 dark:text-white"
                  placeholder="customer@example.com"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setCustomerToEdit(null);
                }}
                disabled={savingEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="px-4 py-2 text-sm font-medium text-black bg-[#FF8C00] hover:bg-[#E67E22] rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {savingEdit && <Loader2 size={16} className="animate-spin" />}
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flag User Modal */}
      {showFlagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Flag User</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Please provide a reason for flagging this user. This will temporarily disable their account.
            </p>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
              rows={4}
              placeholder="Enter reason for flagging this user..."
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
            />
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowFlagModal(false);
                  setFlagReason('');
                  setRiderToFlag(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleFlagUser}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md"
              >
                Flag User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Riders;
